// use-map-ready-state — gates ambient map overlays on MapLibre idle state (J-09)
//
// Phase transitions:
//   initial        → idle (cold-start: map.loaded() && !map.isMoving())
//   initial        → moving  (movestart before first idle)
//   moving         → loading-tiles  (moveend)
//   loading-tiles  → idle  (idle — MapLibre fires this after tiles are rendered)
//   idle           → moving  (movestart)
//
// isReady is true only in the 'idle' phase.

import { useEffect, useState } from 'react';

import type maplibregl from 'maplibre-gl';

export type MapReadyPhase = 'initial' | 'moving' | 'loading-tiles' | 'idle';

export interface MapReadyState {
  /** True only when the map is fully idle (tiles rendered, no active camera animation). */
  isReady: boolean;
  phase: MapReadyPhase;
}

/**
 * Subscribes to MapLibre map lifecycle events and returns the current ready
 * state of the map. Designed for use inside a MapProvider context where the
 * map instance is guaranteed to be non-null.
 *
 * Cleanup: all event listeners are removed on unmount.
 */
export function useMapReadyState(map: maplibregl.Map): MapReadyState {
  // Determine the initial phase synchronously — if the map is already idle
  // on mount (e.g., the hook mounts after the 'load' event fired), start ready.
  const [state, setState] = useState<MapReadyState>(() => {
    const alreadyIdle = map.loaded() && !map.isMoving();
    return {
      isReady: alreadyIdle,
      phase: alreadyIdle ? 'idle' : 'initial',
    };
  });

  useEffect(() => {
    // Re-check synchronously in case the map became idle between render and effect
    if (map.loaded() && !map.isMoving()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setState({ isReady: true, phase: 'idle' });
    }

    function onMoveStart() {
      setState({ isReady: false, phase: 'moving' });
    }

    function onMoveEnd() {
      // Tiles may still be loading — stay false until the idle event
      setState({ isReady: false, phase: 'loading-tiles' });
    }

    function onIdle() {
      setState({ isReady: true, phase: 'idle' });
    }

    map.on('movestart', onMoveStart);
    map.on('moveend', onMoveEnd);
    map.on('idle', onIdle);

    return () => {
      map.off('movestart', onMoveStart);
      map.off('moveend', onMoveEnd);
      map.off('idle', onIdle);
    };
  }, [map]);

  return state;
}
